import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { OpenInNew } from '@mui/icons-material';
import { ColorSet } from 'src/redux/reducers/theme';

interface IPoppins {
  weight?: 400 | 500 | 600;
  px?: 12 | 14 | 16 | 18 | 20 | 22 | 24 | 26 | 28 | 32 | 56;
  color?: keyof ColorSet;
  nowrap?: boolean;
}

export const Poppins = styled.span<IPoppins>`
  position: relative;
  color: ${({ theme }) => theme.colors.prussianBlue};

  ${({ color, theme }) =>
    color &&
    css`
      color: ${theme.colors[color]};
    `};

  ${({ weight }) =>
    weight &&
    css`
      font-weight: ${weight} !important;
    `};

  ${({ nowrap }) =>
    nowrap &&
    css`
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    `};

  ${({ px = 16 }) => css`
    font-size: ${px}px;
  `};

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: ${({ px = 16 }) => px * 0.85}px;
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    font-size: ${({ px = 16 }) => {
      if (px > 30) return 18;
      return px * 0.7;
    }}px;
  }
`;
interface DataTitleProps {
  $noMargin?: boolean;
}

export const DataTitle = styled(Poppins).attrs((p) => {
  return { px: 14, ...p };
})<DataTitleProps>`
  display: block;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.cflowerBlue};

  ${({ $noMargin }) =>
    !$noMargin &&
    css`
      margin-bottom: 6px;
      ${({ theme }) => theme.breakpoints.down('md')} {
        margin-bottom: 0px;
      }
    `};
`;

const DataValueBase = styled(Poppins).attrs((p) => {
  return { px: 14, ...p };
})`
  display: block;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.prussianBlue};
  white-space: pre-wrap;

  a {
    color: ${({ theme }) => theme.colors.prussianBlue};
    text-decoration: underline;
  }
`;

const URL_PATTERN = '(?:https?:\\/\\/|www\\.)[^\\s<>"\')\\]]+';
const URL_REGEX = new RegExp(`(${URL_PATTERN})`, 'gi');

const isUrl = (text: string) => new RegExp(`^${URL_PATTERN}$`, 'i').test(text);

const normalizeHref = (url: string) => (url.startsWith('www.') ? `https://${url}` : url);

const StyledLinkIcon = styled(OpenInNew)`
  font-size: 14px !important;
  color: ${({ theme }) => theme.colors.prussianBlue};
`;

export const linkifyText = (text: string): React.ReactNode => {
  const parts = text.split(URL_REGEX);
  return parts.map((part, index) =>
    isUrl(part) ? (
      <a
        key={index}
        href={normalizeHref(part)}
        target="_blank"
        rel="noopener noreferrer"
        className="table-link"
        data-cy="linkified-text"
      >
        {part} <StyledLinkIcon />
      </a>
    ) : (
      part
    ),
  );
};

type DataValueProps = React.ComponentProps<typeof DataValueBase>;

export const DataValue = forwardRef<HTMLSpanElement, DataValueProps>(({ children, ...props }, ref) => {
  const processedChildren = typeof children === 'string' ? linkifyText(children) : children;

  return (
    <DataValueBase ref={ref} {...props}>
      {processedChildren}
    </DataValueBase>
  );
});

DataValue.displayName = 'DataValue';
